import { type GetProps, styled } from "tamagui";
import { shadow } from "../../themes/token-shadow";
import { SelectionCard } from "../Card/SelectionCard";
import { DecorativeTrailing } from "../DecorativeTrailing";
import { Icon, type IconComponent } from "../Icon";
import { View, XStack, YStack } from "../Stacks";
import { Body2, Subtitle1, Title3 } from "../Typography";
import { useSelectionItem } from "./SelectionContext";
import { renderIndicator } from "./SelectionRenderers";
type CardStyleProps = Pick<GetProps<typeof SelectionCard>, "flex" | "flexBasis" | "flexGrow" | "flexShrink" | "minWidth" | "width">;
type SelectionTileBaseProps = {
  value: string;
  label: string;
  description?: string;
  disabled?: boolean;
  testID?: string;
} & CardStyleProps;

// Tile has 2 variants: prominent mode with just a big title and subtitle and
// then default mode (more similar to SelectionRow) that supports icon +
// trailing + title + description
type SelectionTileProps = SelectionTileBaseProps & ({
  icon?: IconComponent;
  /** Decorative trailing element (badge or pip). Ignored when the group has showSelector enabled. */
  trailing?: DecorativeTrailing;
  prominent?: never;
} | {
  icon?: never;
  trailing?: never;
  prominent: true;
});
const StyledIcon = styled(Icon, {
  name: "TileIcon",
  size: "$iconLarge",
  color: "$iconDefault",
  variants: {
    disabled: {
      true: {
        color: "$iconSubtle"
      }
    }
  }
} as const);
const TileLabel = styled(Subtitle1, {
  name: "TileLabel",
  color: "$textDefault",
  variants: {
    disabled: {
      true: {
        color: "$textSubtle"
      }
    }
  }
} as const);
const TileDescription = styled(Body2, {
  name: "TileDescription",
  color: "$textSubtle",
  variants: {
    disabled: {
      true: {
        color: "$textMuted"
      }
    }
  }
} as const);
const ProminentLabel = styled(Title3, {
  name: "ProminentLabel",
  color: "$textDefault",
  textAlign: "center",
  variants: {
    disabled: {
      true: {
        color: "$textSubtle"
      }
    }
  }
} as const);
const ProminentDescription = styled(Body2, {
  name: "ProminentDescription",
  color: "$textSubtle",
  textAlign: "center",
  variants: {
    disabled: {
      true: {
        color: "$textMuted"
      }
    }
  }
} as const);
const TileAccessoryRow = ({
  icon,
  trailing,
  showSelector,
  mode,
  selected,
  disabled
}: {
  icon?: IconComponent;
  trailing?: DecorativeTrailing;
  showSelector: boolean;
  mode: "radio" | "checkbox" | "switch";
  selected: boolean;
  disabled: boolean;
}) => {
  if (!icon && !trailing && !showSelector) {
    return null;
  }
  return <XStack alignItems="center" justifyContent="space-between" data-sentry-element="XStack" data-sentry-component="TileAccessoryRow" data-sentry-source-file="SelectionTile.tsx">
      {icon ? <StyledIcon icon={icon} disabled={disabled} /> : null}
      {showSelector ? <View pointerEvents="none" aria-hidden={true} marginLeft="auto">
          {renderIndicator({
        mode,
        selected,
        disabled
      })}
        </View> : trailing ? <View pointerEvents="none" aria-hidden={true} marginLeft="auto">
          <DecorativeTrailing {...trailing} />
        </View> : null}
    </XStack>;
};
const TileProminentContent = ({
  label,
  description,
  disabled
}: {
  label: string;
  description?: string;
  disabled: boolean;
}) => <>
    <ProminentLabel disabled={disabled} data-sentry-element="ProminentLabel" data-sentry-source-file="SelectionTile.tsx">{label}</ProminentLabel>
    {description ? <ProminentDescription disabled={disabled}>
        {description}
      </ProminentDescription> : null}
  </>;
const TileDefaultContent = ({
  label,
  description,
  disabled,
  icon,
  trailing,
  showSelector,
  mode,
  selected
}: {
  label: string;
  description?: string;
  disabled: boolean;
  icon?: IconComponent;
  trailing?: DecorativeTrailing;
  showSelector: boolean;
  mode: "radio" | "checkbox" | "switch";
  selected: boolean;
}) => <>
    <TileAccessoryRow icon={icon} trailing={trailing} showSelector={showSelector} mode={mode} selected={selected} disabled={disabled} data-sentry-element="TileAccessoryRow" data-sentry-source-file="SelectionTile.tsx" />
    <YStack gap="$sp0.25" data-sentry-element="YStack" data-sentry-source-file="SelectionTile.tsx">
      <TileLabel disabled={disabled} data-sentry-element="TileLabel" data-sentry-source-file="SelectionTile.tsx">{label}</TileLabel>
      {description ? <TileDescription disabled={disabled}>{description}</TileDescription> : null}
    </YStack>
  </>;
export const SelectionTile = ({
  value,
  label,
  description,
  disabled: disabledProp,
  flex,
  flexBasis,
  flexGrow,
  flexShrink,
  minWidth,
  width,
  prominent,
  icon,
  trailing,
  testID
}: SelectionTileProps) => {
  const {
    group,
    disabled,
    selected,
    onPress
  } = useSelectionItem(value, disabledProp);
  return <SelectionCard testID={testID} selected={selected} disabled={disabled} role={group.mode} aria-label={label} aria-checked={selected} onPress={onPress} flex={flex ?? 1} flexBasis={flexBasis} flexGrow={flexGrow} flexShrink={flexShrink} minWidth={minWidth} width={width} gap={prominent ? "$sp1" : "$sp2"} {...prominent ? {
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: "$sp1.5"
  } : {}} {...prominent && selected ? shadow.medium : {}} data-sentry-element="SelectionCard" data-sentry-component="SelectionTile" data-sentry-source-file="SelectionTile.tsx">
      {prominent ? <TileProminentContent label={label} description={description} disabled={disabled} /> : <TileDefaultContent label={label} description={description} disabled={disabled} icon={icon} trailing={trailing} showSelector={group.showSelector} mode={group.mode} selected={selected} />}
    </SelectionCard>;
};
export type { SelectionTileProps };